/* Collections (MAR-372) — layout the design system had no component for, added for the Ledger.
   Appended to the library through `export * from './Collections'` in ./index.jsx. */
import * as React from 'react';
import { Orb } from './index.jsx';

const cx = (...a) => a.filter(Boolean).join(' ');

/** A vertical stack of cards (Ledger lines, banners, a campaign's people). */
function CardList({ children, as: Tag = 'div', className = '', ...rest }) {
  return <Tag {...rest} className={cx('mv-list', className)}>{children}</Tag>;
}

/** A responsive grid of tiles (Metric, Card + Stat). `sm` packs narrow count tiles. */
function TileGrid({ children, size = 'md', columns, className = '', ...rest }) {
  return <div {...rest} className={cx('mv-tiles', size === 'sm' && 'mv-tiles--sm', columns === 3 && 'mv-tiles--three', columns === 2 && 'mv-tiles--two', className)}>{children}</div>;
}

/**
 * A read-only customer conversation, shown to the merchant: Marva's turns on the left with the
 * orb, the customer's on the right, system lines centred. Unlike `Message` (the merchant's own
 * Ask thread, which announces "You:"), each turn is announced by its real speaker.
 */
function Transcript({ turns = [], label = 'The conversation', className = '' }) {
  return (
    <ol className={cx('mv-transcript', className)} aria-label={label}>
      {turns.map((t, i) => {
        if (t.from === 'system') return <li key={i} className="mv-transcript-sys">{t.text}</li>;
        const customer = t.from === 'customer';
        const speaker = customer ? 'Customer' : 'Marva';
        return (
          <li key={i} className={cx('mv-msg-row', customer ? 'mv-msg-row--user' : 'mv-msg-row--marva')}>
            {!customer && <Orb size={22} />}
            <div className="mv-msg-col">
              <div className={cx('mv-bubble', customer ? 'mv-bubble--user' : 'mv-bubble--marva')}>
                <span className="mv-sr">{speaker}: </span>
                <div className="mv-bubble-plain">{t.text}</div>
              </div>
              <div className="mv-msg-meta" aria-hidden="true">{t.meta || speaker}</div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * A story list (MAR-381): the dense, ruled list the Ledger and Knowledge read as — one line per
 * decision, belief or record, newest or least-sure first. Not a stack of cards: a hairline
 * between rows, a quiet fill on hover. Each `EntryRow` is ONE target.
 */
/* `rail` (MAR-401): a dot beside each row's body on a thin vertical line — a timeline read
   down the page (Knowledge's What she learned). Pair it with each row's `when` (a day label on
   the first row of the day, `""` on the rest) so the rows share one gutter. */
function EntryList({ children, label, rail = false, className = '', ...rest }) {
  return <ul {...rest} className={cx('mv-entries', rail && 'mv-entries--rail', className)} aria-label={label}>{children}</ul>;
}

/**
 * One row of an `EntryList`: `when | body | meta`, collapsing to `when | body` with the meta
 * under it when the LIST is narrow (a container query — the canvas beside the Ask dock, a
 * drawer), and to one column when it is very narrow.
 *
 * The whole row is the target — a real `<button>` (`onOpen`) or `<a>` (`href`); with neither
 * it is a static line. Everything inside is phrasing content, so pass no interactive control
 * in any slot. `title` is the headline, `tag` sits beside it (a `Badge mono`), `why` is one
 * line of reasoning (clamped to one line unless `clamp={false}`), `children` any further body
 * line, `chips` a row of `Chip`s (omitted when empty), `value` a gold value tile with
 * `valueLabel`, `aside` anything else for the meta column (a `Meter`, a `Badge`), and
 * `openLabel` the quiet cue that says what opening it shows. Other props (`data-*`) land on
 * the target.
 */
function EntryRow({
  when, dateTime, title, tag, why, clamp = true, children, chips, value, valueLabel, aside, openLabel,
  onOpen, href, selected = false, disabled = false, testId, className = '', ...rest
}) {
  const hasChips = React.Children.toArray(chips).length > 0;
  const hasMeta = value != null || aside != null || (openLabel && (onOpen || href));
  const action = !!(onOpen || href) && !disabled;
  const cls = cx('mv-entry-row', when != null && 'mv-entry-row--when', hasMeta && 'mv-entry-row--meta',
    action && 'mv-entry-row--action', selected && 'mv-entry-row--selected', className);
  const inner = (
    <>
      {when != null && (dateTime
        ? <time className="mv-entry-when" dateTime={dateTime}>{when}</time>
        : <span className="mv-entry-when">{when}</span>)}
      <span className="mv-entry-body">
        <span className="mv-entry-title">
          <span className="mv-entry-head">{title}</span>
          {tag != null && tag !== false && <span className="mv-entry-tag">{tag}</span>}
        </span>
        {why != null && why !== false && <span className={cx('mv-entry-why', clamp && 'mv-entry-why--clamp')}>{why}</span>}
        {children != null && children !== false && <span className="mv-entry-extra">{children}</span>}
        {hasChips && <span className="mv-entry-chips">{chips}</span>}
      </span>
      {hasMeta && (
        <span className="mv-entry-meta">
          {value != null && (
            <span className="mv-entry-value">
              <span className="mv-entry-value-n">{value}</span>
              {valueLabel && <span className="mv-entry-value-l">{valueLabel}</span>}
            </span>
          )}
          {aside}
          {openLabel && action && <span className="mv-entry-open">{openLabel}</span>}
        </span>
      )}
    </>
  );
  const common = { ...rest, 'data-testid': testId || rest['data-testid'], className: cls, 'aria-current': selected ? 'true' : undefined };
  let target;
  if (href && !disabled) target = <a {...common} href={href} onClick={onOpen}>{inner}</a>;
  else if (onOpen || (href && disabled)) target = <button {...common} type="button" disabled={disabled} onClick={onOpen}>{inner}</button>;
  else target = <div {...common}>{inner}</div>;
  return <li className="mv-entry">{target}</li>;
}

export { CardList, TileGrid, Transcript, EntryList, EntryRow };
